import {
    Form,
    FormField,
    FormSection,
    FormType,
    FilteredValue,
    InputType,
    ReactiveFormChangedField,
    ReactiveFormValue,
} from '@fitogest/reactive-form-builder/interfaces';
import { StorageManager } from '../../../../common/storage-manager.class';
import { FormRequestTypes } from '@fitogest/reactive-form-builder/form-request';

import { FincasModel } from '../../../../models/form-common/fincas.module';
import { SectorModel } from '../../../../models/form-common/sector.model';
import { list } from '@fitogest/reactive-form-builder/utils';

import { GenerateEnvironmentDefinition } from '../environment-variables';
import { ParcelasModel } from 'src/app/models/form-common/parcelas.model';

export class GenerateFormDefinition {
  public formType!: FormType;

  /** Environment flags for feature toggles and labels */
  public readonly env: GenerateEnvironmentDefinition = new GenerateEnvironmentDefinition();

  /** Dropdown data buckets (populated externally) */
  public readonly sectores = list();
  
  public variedades = list();
  
  public readonly year = list();

  public tag: ReactiveFormChangedField<FincasModel> = '';

  /** Useful context */
  public readonly usuario = StorageManager.getUser();

  /** Bound finca + current working model */
  public finca: FincasModel = {} as FincasModel;
  
  public model: ReactiveFormValue<FincasModel> = {} as ReactiveFormValue<FincasModel>;

  /** Reactive-form builder surface */
  public formFieldsSection!: FormSection<object>;
  
  public formFields!: Form<FincasModel>;

  /** Optional exports (kept for parity with other forms) */
  public fieldNames: FormField<any>[] = [];
  
  public fieldLabels: string[] = [];

  constructor(
    type: FormRequestTypes,
    fincaData: FincasModel,
    table: (string[] | string[][])[] | null
  ) {
    this.formType = +type;
    this.finca = fincaData;

    this.formFields = {
      name: 'generate',
      header: '',
      type: this.formType,
      sections: this.getFormSections(),
      table: { title: '', showIndex: false, data: table?.[0], style: table?.[1] },
      uploadButton: {isEnabled: false, label: ''},
      signed: false,
    };
  }

  public getGenerateWordFields(): FormField<any>[] {
    const fields: FormField<any>[] = [
      {
        name: 'id_sector',
        label: this.env.sectorName,
        placeholder: 'TODOS',
        width: 6,
        input: {
          type: InputType.DROPDOWN,
          isEditable: true,
          data: this.sectores,
          dropdownConfiguration: {
            valuePrimaryKey: 'id',
            filter: (sector: FilteredValue<SectorModel>): boolean => 
              sector.value?.id_finca === this.finca.id,
          },
        },
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        order: 1,
      },
      {
        name: 'id_variedad',
        label: 'Variedad',
        placeholder: 'TODAS',
        width: 6,
        input: {
          type: this.env.appName === 'covidai' ? InputType.MULTISELECT : InputType.DROPDOWN,
          isEditable: true,
          data: this.variedades,
          dropdownConfiguration: {
            valuePrimaryKey: 'id',
            filter: (parcela: FilteredValue<ParcelasModel>): boolean => 
              parcela.value?.id_finca === this.finca.id,
          },
        },
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        order: 2,
      },
      {
        name: 'fecha_inicio',
        label: 'Fecha inicio',
        width: 6,
        input: { type: InputType.CALENDAR, isEditable: true },
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        order: 3,
      },
      {
        name: 'fecha_fin',
        label: 'Fecha fin',
        width: 6,
        input: { type: InputType.CALENDAR, isEditable: true },
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        order: 4,
      },
    ];

    // record visible fields for CSV exporters that may introspect these
    const visible = fields.filter(f => f.conditions.isVisible);
    
    this.fieldNames = this.fieldNames.concat(visible);
    this.fieldLabels = this.fieldLabels.concat(visible.map(f => f.label));

    // stable order
    return fields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }

  private getFormSections(): FormSection<any>[] {
    const sections: FormSection<any>[] = [
      {
        name: '',
        width: 12,
        isVisible: true,
        fields: this.getGenerateWordFields(),
        order: 1,
      },
    ];

    // Stable order
    return sections.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }
}
